import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "src/@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/@/components/ui/select";
import { Card, CardHeader, CardContent } from "src/@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "src/@/components/ui/radio-group";
import v2_actionConfigCrm from "src/actions/v2/crm/v2_actionConfigCrm";
import useAuth from "src/hooks/useAuth";
import { SelectZui } from "src/components/zui";
import { OptionsSelect } from "src/@/components/shared";
import { RowInput } from "src/components/forms/RowInput";
import transformQualifyLeadNames from "src/utils/crm.utils";
import { AlertInfo } from "src/@/components/shared/AlertInfo";

const CardChangeValue = ({ block, changeBlock }) => {
  const { t } = useTranslation(['automation', 'common']);
  const { cliente } = useAuth();
  const [selectedAction, setSelectedAction] = useState(block?.data?.funnelAction);
  const [configCrm, setConfigCrm] = useState(null);
  const [option, setOption] = useState(1);
  const [selectedReason, setSelectedReason] = useState(null);

  const labels = {
    1: t('crm.qualification.noInterest', 'Sem interesse'),
    2: t('crm.qualification.cold', 'Frio'),
    3: t('crm.qualification.warm', 'Morno'),
    4: t('crm.qualification.warming', 'Esquentando'),
    5: t('crm.qualification.hot', 'Quente'),
  };

  const radioItems = [
    { value: "editar_valor", id: "valor", label: t('changeValue.options.editValue', 'Alterar Valor') },
    { value: "editar_origem_lead", id: "origem", label: t('changeValue.options.editOrigin', 'Alterar Origem') },
    { value: "editar_qualificacao", id: "qualificacao", label: t('changeValue.options.editQualification', 'Alterar Qualificação') },
    { value: "finalizar_negocio", id: "finalizar", label: t('changeValue.options.finishLead', 'Finalizar Lead') },
    { value: "retomar_lead", id: "retomar", label: t('changeValue.options.resumeLead', 'Retomar Lead') },
    { value: "editar_conversao_ganho", id: "marcarVenda", label: t('changeValue.options.markWon', 'Marcar Venda') },
    { value: "editar_conversao_perda", id: "marcarPerda", label: t('changeValue.options.markLost', 'Marcar Perda') },
    { value: "editar_campanha", id: "editarCampanha", label: t('changeValue.options.editCampaign', 'Editar Campanha') }
  ];

  let newReasons = configCrm?.motivos?.map((item) => {
    return { value: item._id, label: item.name }
  });

  async function buscarConfigCrm(querys) {
    try {
      const result = await v2_actionConfigCrm(cliente._id, querys);
      if (result.data.data) {
        setConfigCrm(result.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  // Carrega a config do CRM ao montar quando a ação já estiver definida,
  // garantindo que os selects (campanha, origem, motivo, etc.) exibam o valor salvo.
  useEffect(() => {
    const acoesQueDependemDoCrm = [
      "editar_origem_lead",
      "finalizar_negocio",
      "editar_qualificacao",
      "editar_conversao_perda",
      "editar_campanha",
    ];
    if (cliente?._id && acoesQueDependemDoCrm.includes(block?.data?.funnelAction)) {
      buscarConfigCrm(`?cliente_id=${cliente._id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente?._id, block?.data?.funnelAction]);

  return (
    <div className="w-full">
      <div className="space-y-6">
        {/* Radio Group to select action */}
        <div>
          <label className="block text-sm font-bold mb-4">{t('changeValue.actionQuestion', 'O que deseja fazer?')}</label>
          <RadioGroup
            className="flex flex-col gap-4"
            value={block?.data?.funnelAction}
            onValueChange={e => {
              let event = { name: "funnelAction", value: e }
              changeBlock(event)
              setSelectedAction(e)
              if (["editar_origem_lead", "finalizar_negocio", "editar_qualificacao", "editar_conversao_perda", "editar_campanha"].includes(e)) {
                let querys = `?cliente_id=${cliente._id}`;
                buscarConfigCrm(querys);
              }
            }}
          >
            {radioItems.map(item => (
              <div key={item.value} className="flex items-center gap-2">
                <RadioGroupItem value={item.value} id={item.value} />
                <label htmlFor={item.id} className="text-sm">{item.label}</label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Conditional Render based on selected action */}
        {block?.data?.funnelAction === "editar_valor" && (
          <div className="flex flex-col gap-1 w-1/4">
            <label htmlFor="valor" className="text-sm font-medium">
              {t('changeValue.dealValueLabel', 'Valor do négocio')}
            </label>
            <Input
              type="text"
              id="valor"
              name="valor"
              value={block?.data?.funnelValueLead}
              onChange={e => {
                let event = { name: "funnelValueLead", value: e.target.value }
                changeBlock(event)
              }}
              placeholder={t('labels.value', { ns: 'common', defaultValue: 'Valor' })}
            />
          </div>
        )}

        {block?.data?.funnelAction === "editar_origem_lead" && (
          <div className="flex flex-col gap-1">
            <label htmlFor="origem" className="text-sm font-medium">
              {t('crm.originLabel', 'Origem')}
            </label>
            <Select
              value={block?.data?.origin}
              onValueChange={e => {
                let event = { name: "origin", value: e }
                changeBlock(event)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('crm.selectOriginPlaceholder', 'Selecione a origem')} />
              </SelectTrigger>
              <SelectContent>
                {configCrm?.origens.map((pipe) => (
                  <SelectItem key={pipe._id} value={pipe._id}>
                    {pipe.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {block?.data?.funnelAction === "editar_qualificacao" && (
          <div className="space-y-4">
            <div className="flex flex-row items-center gap-2">
              <div className="w-1/6">
                <label className="block text-sm font-medium">{t('crm.qualificationLabel', 'Qualificação')}</label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  step="1"
                  value={block?.data?.qualifyLead}
                  onChange={(e) => {
                    let event = { name: "qualifyLead", value: Number(e.target.value) }
                    changeBlock(event)
                  }}
                  className="w-full"
                />
              </div>
              <span>
                {transformQualifyLeadNames(configCrm?.qualificacoes, block?.data?.qualifyLead)}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              <span>
                {labels[block?.qualifyLead] || t('changeValue.invalidScoreMessage', 'Selecione uma pontuação válida.')}
              </span>
            </div>
          </div>
        )}

        {block?.data?.funnelAction === "finalizar_negocio" && (
          <div className="text-sm font-medium text-gray-700">
            <div className='w-2/3'>
              <OptionsSelect
                title={t('changeValue.markAsTitle', 'Marcar como')}
                options={[
                  { value: 1, label: t('changeValue.wonOption', 'Venda'), color: 'bg-green-500' },
                  { value: 2, label: t('changeValue.lostOption', 'Perda'), color: 'bg-red-500' },
                ]}
                value={block?.data.conversionTypeLead}
                changeValue={(e) => {
                  setOption(e);
                  let event = { name: "conversionTypeLead", value: Number(e) }
                  changeBlock(event)
                }}
              />
            </div>
            {option === 2 && (
              <div className="flex flex-col gap-2 mt-4">
                <RowInput
                  label={t('changeValue.lossReasonLabel', 'motivo da perda')}
                  input={
                    <SelectZui
                      value={block?.data.lostReasonLead}
                      list={newReasons}
                      fullWidth
                      onChange={e => {
                        setSelectedReason(e);
                        let event = { name: "lostReasonLead", value: e }
                        changeBlock(event)
                      }}
                      itemSelect={(item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      )}
                    />
                  }
                />
              </div>
            )}
          </div>
        )}

        {block?.data?.funnelAction === "retomar_lead" && (
          <div className="mt-8">
            <AlertInfo
              title={t('status.warning', { ns: 'common', defaultValue: 'Atenção' })}
              description={t('changeValue.resumeLeadWarning', 'Esta ação irá retomar o lead para o funil de vendas, caso ele tenha sido finalizado e marcado uma conversão.')}
            />
          </div>
        )}

        {block?.data?.funnelAction === "editar_conversao_ganho" && (
          <div className="text-sm font-medium text-gray-700">
            {t('changeValue.options.markWon', 'Marcar Venda')}
          </div>
        )}

        {block?.data?.funnelAction === "editar_conversao_perda" && (
          <div className="text-sm font-medium text-gray-700">
            <div className="flex flex-col gap-2 mt-4">
              <RowInput
                label={t('changeValue.lossReasonLabel', 'motivo da perda')}
                input={
                  <SelectZui
                    value={block?.data.lostReasonLead}
                    list={newReasons}
                    fullWidth
                    onChange={e => {
                      setSelectedReason(e);
                      let event = { name: "lostReasonLead", value: e }
                      changeBlock(event)
                    }}
                    itemSelect={(item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    )}
                  />
                }
              />
            </div>
          </div>
        )}

        {block?.data?.funnelAction === "editar_campanha" && (
          <div className="flex flex-col gap-1">
            <label htmlFor="campanha" className="text-sm font-medium">
              {t('crm.campaignLabel', 'Campanha')}
            </label>
            <Select
              value={block?.data?.campanhaId}
              onValueChange={e => {
                let event = { name: "campanhaId", value: e }
                changeBlock(event)
              }}
            >
              <SelectTrigger className="w-full h-9 border-gray-200 hover:border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900 text-sm">
                <SelectValue placeholder={t('crm.selectCampaignPlaceholder', 'Selecione a campanha')} />
              </SelectTrigger>
              <SelectContent>
                {configCrm?.campanhas?.map((campanha) => (
                  <SelectItem key={campanha._id} value={campanha._id}>
                    {campanha.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <AlertInfo
          title={t('status.warning', { ns: 'common', defaultValue: 'Atenção' })}
          description={t('changeValue.noLeadLinkedWarning', 'Se não houver nenhum lead vinculado, esta ação será ignorada.')}
        />
      </div>
    </div>
  );
};

export default CardChangeValue;
